import type { CSSProperties } from 'react'

import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { errorColorFor } from '../shared/errorColor'

export type { ColorScheme }

const tokens = {
  dark: {
    bg: 'bg.input',
    text: 'text.primary',
    placeholder: 'text.secondary',
    border: 'interactive.tertiary-border',
    focusRing: 'interactive.focus-ring',
  },
  light: {
    bg: 'light.bg.input',
    text: 'light.text.primary',
    placeholder: 'light.text.secondary',
    border: 'light.interactive.tertiary-border',
    focusRing: 'light.interactive.focus-ring',
  },
} as const

export const multilineOuterStyles = (
  colorScheme: ColorScheme = 'dark',
  hasSideBorders = true
): SystemStyleObject => ({
  ...(hasSideBorders && {
    borderLeft: '1px solid',
    borderRight: '1px solid',
    borderColor:
      colorScheme === 'dark' ? 'border.subtle' : 'light.border.subtle',
  }),
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  p: '20px',
})

export const multilineLabelStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: colorScheme === 'dark' ? 'text.primary' : 'light.text.primary',
  fontFamily: "'Soehne Mono', sans-serif",
  fontSize: '8px',
  letterSpacing: '1px',
  lineHeight: '12px',
  pb: '4px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
})

export const actionButtonWrapperStyles = (
  visible: boolean
): SystemStyleObject => ({
  display: 'grid',
  gridTemplateRows: visible ? '1fr' : '0fr',
  transition: 'grid-template-rows 0.2s ease',
})

export const actionButtonInnerStyles: SystemStyleObject = {
  overflow: 'hidden',
}

/** Padding and row gap of the bordered field, shared with the resize handle. */
const CONTAINER_PADDING = 16
const CONTAINER_GAP = 10

/** Slack between the 16px hit target and the 12px glyph inside it. */
const HANDLE_GLYPH_INSET = 2

export const multilineContainerStyles = (
  colorScheme: ColorScheme = 'dark',
  isFocused: boolean,
  width: string | number = '100%',
  hasWarning = false
): SystemStyleObject => ({
  bg: tokens[colorScheme].bg,
  border: '1px solid',
  borderColor: hasWarning
    ? errorColorFor(colorScheme)
    : isFocused
    ? tokens[colorScheme].focusRing
    : tokens[colorScheme].border,
  alignItems: 'flex-end',
  display: 'flex',
  flexDirection: 'column',
  gap: `${CONTAINER_GAP}px`,
  p: `${CONTAINER_PADDING}px`,
  transition: 'border-color 0.15s ease',
  width,
})

export const multilineTextareaStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  bg: 'transparent',
  border: 'none',
  color: tokens[colorScheme].text,
  fontFamily: 'Soehne',
  fontSize: '16px',
  lineHeight: '16px',
  minH: '60px',
  outline: 'none',
  resize: 'none',
  w: 'full',
  _placeholder: { color: tokens[colorScheme].placeholder },
})

/**
 * Applied inline rather than through the emotion class so the dragged height
 * wins over `multilineTextareaStyles` and over the height
 * `useAutoResizeTextarea` last wrote imperatively.
 */
export const resizedTextareaStyles = (height: number | null): CSSProperties =>
  height === null ? {} : { height: `${height}px`, overflowY: 'auto' }

export const textareaWrapperStyles: SystemStyleObject = {
  position: 'relative',
  w: 'full',
}

export const resizeHandleStyles = (
  colorScheme: ColorScheme = 'dark',
  isResizing = false,
  hasRowBelow = false
): SystemStyleObject => ({
  alignItems: 'center',
  // The handle is anchored to the textarea, which sits above the container's
  // row gap and bottom padding, so clearing both is what lands the glyph in
  // the bordered field's corner, where the native textarea grabber was. When a
  // row renders below (action button or warning text) that corner is taken, so
  // the handle stays just inside the textarea instead.
  bottom: hasRowBelow
    ? `-${HANDLE_GLYPH_INSET}px`
    : `-${CONTAINER_PADDING + CONTAINER_GAP - HANDLE_GLYPH_INSET}px`,
  right: `-${CONTAINER_PADDING - HANDLE_GLYPH_INSET}px`,
  color: isResizing
    ? tokens[colorScheme].text
    : tokens[colorScheme].placeholder,
  cursor: 'ns-resize',
  display: 'flex',
  justifyContent: 'center',
  position: 'absolute',
  boxSize: '16px',
  touchAction: 'none',
  transition: 'color 0.15s ease',
  _hover: { color: tokens[colorScheme].text },
  _focusVisible: {
    color: tokens[colorScheme].text,
    outline: '1px solid',
    outlineColor: tokens[colorScheme].focusRing,
  },
})

export const multilineWarningTextStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: errorColorFor(colorScheme),
  fontSize: '8px',
  fontWeight: '600',
  letterSpacing: '0.08em',
  lineHeight: 1,
  pt: '4px',
  textTransform: 'uppercase',
})
